import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCirclePlus, IconExternalLink, IconTrash } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

const CreateSchema = z.object({
  title: z.string().trim().min(2).max(200),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'kebab-case')
    .max(120),
});
type CreateForm = z.infer<typeof CreateSchema>;

function CmsPagesIndex() {
  const list = trpc.admin.pages.list.useQuery();
  const utils = trpc.useUtils();
  const [opened, { open, close }] = useDisclosure(false);
  const create = trpc.admin.pages.create.useMutation({
    onSuccess: () => {
      void utils.admin.pages.list.invalidate();
      close();
      notifications.show({ message: 'Page created', color: 'green' });
      form.reset();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const del = trpc.admin.pages.delete.useMutation({
    onSuccess: () => void utils.admin.pages.list.invalidate(),
  });
  const form = useForm<CreateForm>({ resolver: zodResolver(CreateSchema) });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>CMS pages</Title>
          <Button leftSection={<IconCirclePlus size={16} />} onClick={open}>
            New page
          </Button>
        </Group>
        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={200} m="md" />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Slug</Table.Th>
                  <Table.Th>Menu</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(list.data ?? []).map((p) => (
                  <Table.Tr key={p.id}>
                    <Table.Td>
                      <Anchor
                        renderRoot={(props) => (
                          <Link to="/central/cms-pages/$uid" params={{ uid: p.uid }} {...props} />
                        )}
                      >
                        {p.title}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Anchor href={`/page/${p.slug}`} target="_blank" size="sm">
                        /page/{p.slug} <IconExternalLink size={12} style={{ display: 'inline' }} />
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      {p.showInMenu === 1 && (
                        <Badge size="xs" variant="light">
                          shown
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td align="right">
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => del.mutate({ pageId: p.id })}
                        aria-label="Delete"
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(list.data ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text c="dimmed" ta="center" py="md">
                        No pages yet.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>
        <Modal opened={opened} onClose={close} title="New page" centered>
          <form onSubmit={form.handleSubmit((v) => create.mutate(v))}>
            <Stack gap="sm">
              <TextInput label="Title" {...form.register('title')} />
              <TextInput
                label="Slug"
                {...form.register('slug')}
                error={form.formState.errors.slug?.message}
              />
              <Button type="submit" loading={create.isPending}>
                Create
              </Button>
            </Stack>
          </form>
        </Modal>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/central/cms-pages')({ component: CmsPagesIndex });
